import React, { useEffect, useRef } from 'react';
import { win } from './win';

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useIsMountedRef = (): React.MutableRefObject<boolean> => {
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return isMountedRef;
};

type ClearTimeout = () => void;

export const useTimeout = (params: {
  onTimeout(): void;
  timeout: number;
}): ClearTimeout => {
  const { onTimeout, timeout } = params;
  const timeoutRef = useRef<{ clear: ClearTimeout; isCleared: boolean }>({
    isCleared: false,
    clear: () => {
      timeoutRef.current.isCleared = true;
    },
  });

  useEffect(() => {
    // could be cleared immediately upon initial render, before this effect invocation
    if (timeoutRef.current.isCleared) {
      return;
    }

    let timeoutId = win.setTimeout(() => {
      if (!timeoutRef.current.isCleared) {
        timeoutId = null;
        timeoutRef.current.isCleared = true;
        onTimeout();
      }
    }, timeout);

    timeoutRef.current.clear = () => {
      if (!timeoutRef.current.isCleared) {
        win.clearTimeout(timeoutId);
        timeoutId = null;
      }

      timeoutRef.current.isCleared = true;
    };

    return timeoutRef.current.clear;
  }, []);

  return timeoutRef.current.clear;
};
